import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  image: string;
  primaryOrganization: {
    id: string;
    name: string;
    slug: string | null;
  } | null;
}

export interface UseAuthReturn {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

/**
 * Single source of truth for the authenticated user.
 *
 * Wraps queryKey: ['profile'] so all callers share one cached request.
 * Returns null user while loading; components should redirect to /login if
 * isAuthenticated is false after isLoading settles.
 *
 * staleTime: 0 — always re-validates on window focus / reconnect so a
 * session expiry or remote logout is caught quickly rather than after the
 * 5-min cache window.
 */
export function useAuth(): UseAuthReturn {
  const { data, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.getProfile(),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
  });

  const profile = data?.userProfile ?? null;

  const user: AuthUser | null = profile
    ? {
        id: profile.id ?? '',
        email: profile.email ?? '',
        name: profile.name ?? '',
        image: profile.image ?? '',
        primaryOrganization:
          profile.primaryOrganization != null
            ? {
                id: profile.primaryOrganization.id ?? '',
                name: profile.primaryOrganization.name ?? '',
                slug: profile.primaryOrganization.slug ?? null,
              }
            : null,
      }
    : null;

  return { user, isLoading, isAuthenticated: user !== null };
}
